import { useLocation, useNavigate } from "react-router-dom";
import {
  Briefcase,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Filter,
  MapPin,
  Search,
  Trash2,
  TrendingUp,
  Users,
} from "lucide-react";
import { HiringRoleStatusGlyph } from "../components/HiringRoleStatusGlyph";
import RecruitmentShell from "../components/RecruitmentShell";
import JobuwinPageLoading from "../components/JobuwinPageLoading";
import HiringBuyJobsModal, { HiringManagePlanModal } from "../components/HiringBuyJobsModal";
import { RecruiterApplicantIcon } from "../components/RecruiterApplicantIcons";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { hiringRoleStatusLabel } from "../data/hiringOpenRoles";
import {
  deleteHiringJobPostDraft,
  getHiringApplicantStats,
  getHiringBillingSummary,
  getHiringJobPostStatusCounts,
  listHiringJobPosts,
} from "../apiClient";
import { hiringBillingStatCard } from "../lib/hiringBillingDisplay";
import { HIRING_LAUNCH_FREE_MODE } from "../lib/hiringLaunchMode";

const DASHBOARD_PAGE_LIMIT = 5;

const DEFAULT_JOB_POST_STATUS_TOTALS = Object.freeze({
  active: 0,
  hidden: 0,
  closed: 0,
  draft: 0,
});

const DEFAULT_APPLICANT_STATS = Object.freeze({
  totalApplicants: 0,
  newThisWeek: 0,
  totalNewApplicants: 0,
});

function jobPostStatusTotalsFromSnapshot(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_JOB_POST_STATUS_TOTALS };
  }
  return {
    active: Math.max(0, Math.floor(Number(raw.active)) || 0),
    hidden: Math.max(0, Math.floor(Number(raw.hidden)) || 0),
    closed: Math.max(0, Math.floor(Number(raw.closed)) || 0),
    draft: Math.max(0, Math.floor(Number(raw.draft)) || 0),
  };
}

const DEFAULT_HIRING_ROLE_STATUS_FILTER = {
  active: true,
  hidden: true,
  closed: true,
  draft: true,
};

const DEFAULT_HIRING_ROLE_SORT = "advertised-desc";

const HIRING_ROLE_SORT_OPTIONS = [
  { value: "new-applications-desc", primary: "New applications", secondary: "Most first" },
  { value: "applicants-desc", primary: "Applicants", secondary: "Most first" },
  { value: "advertised-desc", primary: "Recent advert", secondary: "Newest first" },
  { value: "advertised-asc", primary: "Oldest post", secondary: "First published" },
];

const HIRING_ROLE_STATUS_OPTIONS = [
  { key: "active", label: "Active" },
  { key: "hidden", label: "Hidden" },
  { key: "closed", label: "Closed" },
  { key: "draft", label: "Drafts" },
];

const METRIC_COLUMNS = [
  { key: "newApplications", icon: "new-user", label: "New", index: 0, highlight: true },
  { key: "rejectedApplications", icon: "rejected-applicants", label: "Rejected", index: 1 },
  { key: "shortlistedApplicants", icon: "accepted-applicant", label: "Shortlisted", index: 2 },
  { key: "parkApplicants", icon: "park-applicant", label: "Parked", index: 3 },
  { key: "applicants", icon: "applicants", label: "All", index: 4 },
];

const HIRING_DASHBOARD_SNAPSHOT_KEY = "jobuwin-hiring-dashboard-snapshot-v1";

function readHiringDashboardSnapshot(loc) {
  if (!loc?.state?.restoreHiringDashboard) return null;
  try {
    const raw = sessionStorage.getItem(HIRING_DASHBOARD_SNAPSHOT_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== "object") return null;
    return data;
  } catch {
    return null;
  }
}

function saveHiringDashboardSnapshot(payload) {
  try {
    sessionStorage.setItem(HIRING_DASHBOARD_SNAPSHOT_KEY, JSON.stringify(payload));
  } catch {
    /* quota or private mode */
  }
}

function stripRestoreFromLocationState(state) {
  if (!state || typeof state !== "object") return null;
  const { restoreHiringDashboard: _drop, preserveScroll: _ps, ...rest } = state;
  return Object.keys(rest).length ? rest : null;
}

function buildDashboardReturnState(currentLocState, extra = {}) {
  const prev = currentLocState && typeof currentLocState === "object" ? { ...currentLocState } : {};
  if (extra && typeof extra === "object") {
    Object.assign(prev, extra);
  }
  delete prev.restoreHiringDashboard;
  delete prev.preserveScroll;
  return { ...prev, restoreHiringDashboard: true };
}

function stripHtmlToPlainText(html) {
  return String(html || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function timeValueToMs(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : 0;
  }
  if (typeof value === "object" && typeof value.getTime === "function") {
    const t = value.getTime();
    return Number.isFinite(t) ? t : 0;
  }
  return 0;
}

function daysSinceIso(value) {
  const ms = timeValueToMs(value);
  if (!ms) return 999999;
  return Math.max(0, Math.floor((Date.now() - ms) / 86400000));
}

function parseIsoMs(iso) {
  return timeValueToMs(iso);
}

function formatJobPostUpdatedLabel(value) {
  const ms = timeValueToMs(value);
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value || "").trim();
  }
}

function formatRelativeUpdated(value) {
  const ms = timeValueToMs(value);
  if (!ms) return "";
  const diffSec = Math.floor((Date.now() - ms) / 1000);
  if (diffSec < 60) return "Updated just now";
  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `Updated ${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Updated ${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `Updated ${days} day${days === 1 ? "" : "s"} ago`;
  return `Updated ${formatJobPostUpdatedLabel(value)}`;
}

function safeNonNegativeInt(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(Math.floor(n), 1_000_000_000);
}

function normalizeJobPostToRoleCard(jobPost) {
  const summaryPlain = stripHtmlToPlainText(jobPost?.summary);
  const raw = String(jobPost?.status || "").toLowerCase();
  let status = "active";
  if (raw === "draft") status = "draft";
  else if (raw === "archived") status = "closed";
  else if (raw === "published" && jobPost?.listedInApplicantSearch === false) status = "hidden";
  const advertRef = jobPost?.publishedAt || jobPost?.updatedAt;
  const advertSortMs =
    raw === "draft" ? timeValueToMs(jobPost?.updatedAt) : timeValueToMs(jobPost?.publishedAt || jobPost?.updatedAt);

  const city = String(jobPost?.city || "").trim();
  const country = String(jobPost?.country || "").trim();
  const location = [city, country].filter(Boolean).join(", ");

  const publishedAtMs = parseIsoMs(jobPost?.publishedAt);
  const updatedAtMs = parseIsoMs(jobPost?.updatedAt);

  return {
    id: String(jobPost?.id || ""),
    title: String(jobPost?.title || "").trim() || "Untitled role",
    status,
    summaryPlain,
    postedDaysAgo: daysSinceIso(raw === "draft" ? jobPost?.updatedAt : advertRef),
    advertSortMs,
    publishedAtMs,
    updatedAtMs,
    location,
    updatedAt: jobPost?.updatedAt,
    newApplications: safeNonNegativeInt(jobPost?.newApplications),
    rejectedApplications: safeNonNegativeInt(jobPost?.rejectedApplications),
    shortlistedApplicants: safeNonNegativeInt(jobPost?.shortlistedApplicants),
    parkApplicants: safeNonNegativeInt(jobPost?.parkApplicants),
    applicants: safeNonNegativeInt(jobPost?.applicants),
    jobPostId: String(jobPost?.id || ""),
    listingJobId: raw === "published" ? String(jobPost?.id || "") : "",
  };
}

function countActiveStatusFilters(statusFilter) {
  return HIRING_ROLE_STATUS_OPTIONS.reduce(
    (count, { key }) => count + (statusFilter[key] ? 1 : 0),
    0
  );
}

function HiringDashboardFiltersPanel({
  draftStatusFilter,
  setDraftStatusFilter,
  draftSortBy,
  setDraftSortBy,
  jobPostStatusTotals,
  onApply,
  onClear,
  onClose,
}) {
  return (
    <div id="hiring-dashboard-filters-panel" className="nd1-hiring-filters-panel" role="region" aria-label="Filters">
      <div className="nd1-hiring-filters-panel__head">
        <p className="nd1-hiring-filters-panel__section-label">Status</p>
        <button
          type="button"
          className="nd1-hiring-filters-panel__collapse"
          onClick={onClose}
          aria-label="Collapse filters"
        >
          <ChevronUp size={18} aria-hidden="true" />
        </button>
      </div>

      <div className="nd1-hiring-filters-status-row" role="group" aria-label="Status">
        {HIRING_ROLE_STATUS_OPTIONS.map(({ key, label }) => {
          const selected = draftStatusFilter[key];
          return (
            <label
              key={key}
              className={`nd1-hiring-filters-status-pill nd1-hiring-filters-status-pill--${key}${
                selected ? " nd1-hiring-filters-status-pill--selected" : ""
              }`}
            >
              <input
                type="checkbox"
                className="checkbox-control checkbox-control--primary-2 nd1-hiring-filters-status-pill__checkbox"
                checked={selected}
                onChange={(event) => {
                  setDraftStatusFilter((current) => ({
                    ...current,
                    [key]: event.target.checked,
                  }));
                }}
              />
              <HiringRoleStatusGlyph variant={key} size={14} />
              <span className="nd1-hiring-filters-status-pill__label">
                {label} ({jobPostStatusTotals[key]})
              </span>
            </label>
          );
        })}
      </div>

      <p className="nd1-hiring-filters-panel__section-label nd1-hiring-filters-panel__section-label--sort">
        Order by
      </p>

      <div className="nd1-hiring-filters-sort" role="radiogroup" aria-label="Order by">
        {HIRING_ROLE_SORT_OPTIONS.map((option) => {
          const selected = draftSortBy === option.value;
          return (
            <label
              key={option.value}
              className={`nd1-hiring-filters-sort-pill${selected ? " nd1-hiring-filters-sort-pill--selected" : ""}`}
            >
              <input
                type="radio"
                name="hiring-dashboard-sort"
                className="nd1-hiring-filters-sort-pill__input"
                value={option.value}
                checked={selected}
                onChange={() => setDraftSortBy(option.value)}
              />
              <span className="nd1-hiring-filters-sort-pill__primary">{option.primary}</span>
              <span className="nd1-hiring-filters-sort-pill__secondary">({option.secondary})</span>
            </label>
          );
        })}
      </div>

      <div className="nd1-hiring-filters-panel__actions">
        <button type="button" className="nd1-btn nd1-btn--outline nd1-hiring-filters-panel__clear" onClick={onClear}>
          Reset
        </button>
        <button type="button" className="nd1-btn nd1-btn--accent" onClick={onApply}>
          Apply Filters
        </button>
      </div>
    </div>
  );
}

function HiringDashboardPage({ currentUser, setCurrentUser, authToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const initialSnap = useMemo(
    () => readHiringDashboardSnapshot(location),
    [location.key, location.state?.restoreHiringDashboard]
  );
  const skipInitialListLoadRef = useRef(Boolean(initialSnap));

  const displayName = currentUser?.name || "there";
  const [roleSearchInput, setRoleSearchInput] = useState(() => initialSnap?.roleSearchInput ?? "");
  const [roleSearchTerm, setRoleSearchTerm] = useState(() => initialSnap?.roleSearchTerm ?? "");
  const [roleStatusFilter, setRoleStatusFilter] = useState(() => {
    const s = initialSnap?.roleStatusFilter;
    if (s && typeof s === "object") {
      return {
        active: Boolean(s.active),
        hidden: Boolean(s.hidden),
        closed: Boolean(s.closed),
        draft: Boolean(s.draft),
      };
    }
    return { ...DEFAULT_HIRING_ROLE_STATUS_FILTER };
  });
  const [roleSortBy, setRoleSortBy] = useState(() => initialSnap?.roleSortBy ?? DEFAULT_HIRING_ROLE_SORT);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [draftStatusFilter, setDraftStatusFilter] = useState(() => {
    const s = initialSnap?.roleStatusFilter;
    if (s && typeof s === "object") {
      return {
        active: Boolean(s.active),
        hidden: Boolean(s.hidden),
        closed: Boolean(s.closed),
        draft: Boolean(s.draft),
      };
    }
    return { ...DEFAULT_HIRING_ROLE_STATUS_FILTER };
  });
  const [draftSortBy, setDraftSortBy] = useState(() => initialSnap?.roleSortBy ?? DEFAULT_HIRING_ROLE_SORT);
  const [jobPosts, setJobPosts] = useState(() =>
    Array.isArray(initialSnap?.jobPosts) ? initialSnap.jobPosts : []
  );
  const [pageInfo, setPageInfo] = useState(() => {
    const p = initialSnap?.pageInfo;
    if (p && typeof p === "object") {
      return { hasMore: Boolean(p.hasMore), nextCursor: p.nextCursor || null };
    }
    return { hasMore: false, nextCursor: null };
  });
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState("");
  const [deletingDraftId, setDeletingDraftId] = useState(null);
  const [draftDeleteModal, setDraftDeleteModal] = useState(null);
  const [visibleCount, setVisibleCount] = useState(() => {
    const v = initialSnap?.visibleCount;
    return typeof v === "number" && Number.isFinite(v) && v >= 1 ? Math.floor(v) : 5;
  });
  const [jobPostStatusTotals, setJobPostStatusTotals] = useState(() =>
    jobPostStatusTotalsFromSnapshot(initialSnap?.jobPostStatusTotals)
  );
  const [applicantStats, setApplicantStats] = useState({ ...DEFAULT_APPLICANT_STATS });
  const [applicantStatsLoading, setApplicantStatsLoading] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [buyJobsModalOpen, setBuyJobsModalOpen] = useState(false);
  const [managePlanModalOpen, setManagePlanModalOpen] = useState(false);
  const loadMoreSentinelRef = useRef(null);
  const sortedRolesRef = useRef([]);
  const pageInfoRef = useRef({ hasMore: false, nextCursor: null });
  const loadingJobsRef = useRef(false);
  const visibleCountRef = useRef(5);
  const loadJobsFnRef = useRef(async () => {});

  useEffect(() => {
    pageInfoRef.current = pageInfo;
  });
  useEffect(() => {
    loadingJobsRef.current = loadingJobs;
  });
  useEffect(() => {
    visibleCountRef.current = visibleCount;
  });

  useEffect(() => {
    if (!filtersOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setFiltersOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [filtersOpen]);

  useLayoutEffect(() => {
    if (!initialSnap) return undefined;
    try {
      sessionStorage.removeItem(HIRING_DASHBOARD_SNAPSHOT_KEY);
    } catch {
      /* ignore */
    }
    const scrollY =
      typeof initialSnap.scrollY === "number" && Number.isFinite(initialSnap.scrollY) ? initialSnap.scrollY : 0;
    const nextState = stripRestoreFromLocationState(location.state);
    const mergedState =
      nextState && typeof nextState === "object" && Object.keys(nextState).length
        ? { ...nextState, preserveScroll: true }
        : { preserveScroll: true };
    navigate(`${location.pathname}${location.search || ""}`, { replace: true, state: mergedState });
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
    });
    return undefined;
  }, [initialSnap, location.pathname, location.search, location.state, navigate]);

  const keyword = roleSearchTerm.trim().toLowerCase();

  const rolesMatchingKeyword = useMemo(() => {
    const roles = jobPosts.map(normalizeJobPostToRoleCard);
    return roles.filter((role) => {
      const idHay = String(role.id || "").toLowerCase().replace(/-/g, "");
      const hay = `${role.title} ${role.summaryPlain || ""} ${role.location || ""} ${role.id}`.toLowerCase();
      const kw = keyword.replace(/-/g, "");
      return !keyword || hay.includes(keyword) || (kw.length >= 4 && idHay.includes(kw));
    });
  }, [jobPosts, keyword]);

  const totalAdvertisedRolesCount = useMemo(() => {
    const fromServer =
      jobPostStatusTotals.active +
      jobPostStatusTotals.hidden +
      jobPostStatusTotals.closed +
      jobPostStatusTotals.draft;
    if (fromServer > 0) return fromServer;
    return rolesMatchingKeyword.length;
  }, [jobPostStatusTotals, rolesMatchingKeyword.length]);

  const filteredRoles = rolesMatchingKeyword.filter((role) => roleStatusFilter[role.status] === true);

  const sortedRoles = useMemo(() => {
    const list = [...filteredRoles];
    const idAsc = (a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
    const titleAsc = (a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" });

    const byApplicants = (a, b) => {
      const d = b.applicants - a.applicants;
      if (d !== 0) return d;
      const n = b.newApplications - a.newApplications;
      if (n !== 0) return n;
      const p = b.publishedAtMs - a.publishedAtMs;
      if (p !== 0) return p;
      const u = b.updatedAtMs - a.updatedAtMs;
      if (u !== 0) return u;
      return titleAsc(a, b) || idAsc(a, b);
    };
    const byNewApps = (a, b) => {
      const d = b.newApplications - a.newApplications;
      if (d !== 0) return d;
      const u = b.updatedAtMs - a.updatedAtMs;
      if (u !== 0) return u;
      const p = b.publishedAtMs - a.publishedAtMs;
      if (p !== 0) return p;
      return titleAsc(a, b) || idAsc(a, b);
    };
    const byNewestAdvertisedFirst = (a, b) => {
      const d = b.advertSortMs - a.advertSortMs;
      if (d !== 0) return d;
      return titleAsc(a, b) || idAsc(a, b);
    };
    const byOldestAdvertisedFirst = (a, b) => {
      const d = a.advertSortMs - b.advertSortMs;
      if (d !== 0) return d;
      return titleAsc(a, b) || idAsc(a, b);
    };

    switch (roleSortBy) {
      case "new-applications-desc":
        return list.sort(byNewApps);
      case "applicants-desc":
        return list.sort(byApplicants);
      case "advertised-asc":
        return list.sort(byOldestAdvertisedFirst);
      case "advertised-desc":
      default:
        return list.sort(byNewestAdvertisedFirst);
    }
  }, [filteredRoles, roleSortBy]);

  useEffect(() => {
    sortedRolesRef.current = sortedRoles;
  }, [sortedRoles]);

  const visibleRoles = useMemo(
    () => sortedRoles.slice(0, visibleCount),
    [sortedRoles, visibleCount]
  );

  const runLoadMore = useCallback(() => {
    if (loadingJobsRef.current) return;
    const sorted = sortedRolesRef.current;
    const vis = visibleCountRef.current;
    if (vis < sorted.length) {
      setVisibleCount((prev) => Math.min(prev + DASHBOARD_PAGE_LIMIT, sorted.length));
      return;
    }
    const pg = pageInfoRef.current;
    if (pg.hasMore && pg.nextCursor) {
      void loadJobsFnRef.current({ cursor: pg.nextCursor });
    }
  }, []);

  const isFullyLoadedEnd =
    sortedRoles.length > 0 && visibleCount >= sortedRoles.length && !pageInfo.hasMore;

  const showLoadMoreButton =
    !jobsError &&
    !isFullyLoadedEnd &&
    (visibleCount < sortedRoles.length || Boolean(pageInfo.hasMore && pageInfo.nextCursor)) &&
    !(loadingJobs && jobPosts.length > 0);

  const loadJobs = useCallback(
    async ({ cursor = null, replace = false } = {}) => {
      const token = String(authToken || "").trim();
      if (!token) return;
      setJobsError("");
      setLoadingJobs(true);
      try {
        const { jobPosts: rows, page } = await listHiringJobPosts(
          {
            limit: DASHBOARD_PAGE_LIMIT,
            cursor: cursor || undefined,
          },
          token
        );
        setJobPosts((prev) => (replace ? rows : [...prev, ...rows]));
        setPageInfo({
          hasMore: Boolean(page?.hasMore),
          nextCursor: page?.nextCursor || null,
        });
      } catch (err) {
        setJobsError(err instanceof Error ? err.message : "Could not load your roles right now.");
        setPageInfo({ hasMore: false, nextCursor: null });
      } finally {
        setLoadingJobs(false);
      }
    },
    [authToken]
  );

  const refreshJobPostStatusTotals = useCallback(async () => {
    const token = String(authToken || "").trim();
    if (!token) {
      setJobPostStatusTotals({ ...DEFAULT_JOB_POST_STATUS_TOTALS });
      return;
    }
    try {
      const counts = await getHiringJobPostStatusCounts(token);
      setJobPostStatusTotals(counts);
    } catch {
      /* keep existing totals on failure */
    }
  }, [authToken]);

  const refreshApplicantStats = useCallback(async () => {
    const token = String(authToken || "").trim();
    if (!token) {
      setApplicantStats({ ...DEFAULT_APPLICANT_STATS });
      return;
    }
    setApplicantStatsLoading(true);
    try {
      const stats = await getHiringApplicantStats(token);
      setApplicantStats(stats);
    } catch {
      /* keep existing stats on failure */
    } finally {
      setApplicantStatsLoading(false);
    }
  }, [authToken]);

  const refreshBillingSummary = useCallback(async () => {
    const token = String(authToken || "").trim();
    if (!token) {
      setBillingSummary(null);
      return;
    }
    setBillingLoading(true);
    try {
      const summary = await getHiringBillingSummary(token);
      setBillingSummary(summary);
    } catch {
      /* keep existing billing on failure */
    } finally {
      setBillingLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    void refreshJobPostStatusTotals();
    void refreshApplicantStats();
    void refreshBillingSummary();
  }, [refreshJobPostStatusTotals, refreshApplicantStats, refreshBillingSummary]);

  useEffect(() => {
    loadJobsFnRef.current = loadJobs;
  }, [loadJobs]);

  useEffect(() => {
    if (skipInitialListLoadRef.current) {
      skipInitialListLoadRef.current = false;
      return;
    }
    setJobPosts([]);
    setPageInfo({ hasMore: false, nextCursor: null });
    setVisibleCount(5);
    void loadJobs({ replace: true });
  }, [authToken, loadJobs]);

  useEffect(() => {
    setVisibleCount((prev) => {
      const n = sortedRoles.length;
      if (n === 0) return prev;
      return Math.min(prev, n);
    });
  }, [sortedRoles.length]);

  useEffect(() => {
    const el = loadMoreSentinelRef.current;
    if (!el) return undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) runLoadMore();
      },
      { root: null, rootMargin: "280px 0px", threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [jobPosts.length, sortedRoles.length, loadingJobs, runLoadMore]);

  const buildHiringDashboardSnapshot = () => ({
    jobPosts,
    pageInfo,
    roleSearchInput,
    roleSearchTerm,
    roleStatusFilter,
    roleSortBy,
    visibleCount,
    jobPostStatusTotals,
    scrollY: typeof window !== "undefined" ? window.scrollY : 0,
  });

  const openDraftDeleteModal = useCallback((event, role) => {
    event.preventDefault();
    event.stopPropagation();
    const postId = String(role?.jobPostId || role?.id || "").trim();
    if (!postId) return;
    const title = String(role?.title || "this draft").trim() || "this draft";
    setDraftDeleteModal({ postId, title });
  }, []);

  const closeDraftDeleteModal = useCallback(() => {
    if (deletingDraftId) return;
    setDraftDeleteModal(null);
  }, [deletingDraftId]);

  const confirmDraftDelete = useCallback(async () => {
    if (!draftDeleteModal) return;
    const { postId } = draftDeleteModal;
    const token = String(authToken || "").trim();
    if (!token) {
      setJobsError("You need to be signed in to delete a draft.");
      setDraftDeleteModal(null);
      return;
    }
    setJobsError("");
    setDeletingDraftId(postId);
    try {
      await deleteHiringJobPostDraft(postId, token);
      setDraftDeleteModal(null);
      setJobPosts((prev) => prev.filter((p) => String(p?.id) !== postId));
      void refreshJobPostStatusTotals();
    } catch (err) {
      setJobsError(err instanceof Error ? err.message : "Could not delete that draft.");
    } finally {
      setDeletingDraftId(null);
    }
  }, [authToken, draftDeleteModal, refreshJobPostStatusTotals]);

  useEffect(() => {
    if (!draftDeleteModal) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDraftDeleteModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [draftDeleteModal, closeDraftDeleteModal]);

  const handleViewJobPost = (event, role) => {
    event.preventDefault();
    event.stopPropagation();
    if (!role?.id) return;
    if (role.status === "draft") {
      navigate(`/hiring/jobs/new?draft=${encodeURIComponent(role.jobPostId || role.id)}`, { replace: false });
      return;
    }
    saveHiringDashboardSnapshot(buildHiringDashboardSnapshot());
    navigate(`/jobs/${encodeURIComponent(role.jobPostId || role.id)}`, {
      replace: false,
      state: {
        fromPath: `${location.pathname}${location.search || ""}`,
        fromLabel: "Hiring dashboard",
        returnState: buildDashboardReturnState(location.state),
      },
    });
  };

  const persistDashboardAndGoToRoleWorkspace = (roleId, extraState = {}) => {
    saveHiringDashboardSnapshot(buildHiringDashboardSnapshot());
    navigate(`/hiring/roles/${encodeURIComponent(roleId)}`, {
      state: buildDashboardReturnState(location.state, extraState),
    });
  };

  const handleRoleRowActivate = (role) => {
    if (!role?.id) return;
    if (role.status === "draft") {
      navigate(`/hiring/jobs/new?draft=${encodeURIComponent(role.jobPostId || role.id)}`, { replace: false });
      return;
    }
    persistDashboardAndGoToRoleWorkspace(role.id);
  };

  const handleRoleRowClick = (event, role) => {
    if (
      event.target.closest?.(
        "button, a"
      )
    ) {
      return;
    }
    handleRoleRowActivate(role);
  };

  const handleRoleRowKeyDown = (event, role) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleRoleRowActivate(role);
  };

  const getRoleRowAriaLabel = (role) => {
    const title = String(role?.title || "role").trim() || "role";
    if (role?.status === "draft") return `Edit draft for ${title}`;
    if (role?.status === "closed") return `View ${title}`;
    return `Manage ${title}`;
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setRoleSearchInput(value);
    setRoleSearchTerm(value);
    setVisibleCount(5);
  };

  const activeStatusFilterCount = countActiveStatusFilters(roleStatusFilter);

  const toggleFiltersPanel = () => {
    setFiltersOpen((open) => {
      if (!open) {
        setDraftStatusFilter({ ...roleStatusFilter });
        setDraftSortBy(roleSortBy);
      }
      return !open;
    });
  };

  const applyFilters = () => {
    setRoleStatusFilter({ ...draftStatusFilter });
    setRoleSortBy(draftSortBy);
    setVisibleCount(5);
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setDraftStatusFilter({ ...DEFAULT_HIRING_ROLE_STATUS_FILTER });
    setDraftSortBy(DEFAULT_HIRING_ROLE_SORT);
  };

  const draftSubtext = `${jobPostStatusTotals.draft} draft • ${jobPostStatusTotals.closed} closed`;

  const billingStat = useMemo(() => hiringBillingStatCard(billingSummary), [billingSummary]);

  const openBillingCta = (mode) => {
    if (HIRING_LAUNCH_FREE_MODE && (mode === "account" || mode === "buy")) {
      navigate("/purchases");
      return;
    }
    if (mode === "manage") {
      setManagePlanModalOpen(true);
      return;
    }
    setBuyJobsModalOpen(true);
  };

  const renderMetricCell = (role, column) => {
    if (role.status === "draft") {
      return <span className="nd1-hiring-table__metric-dash">—</span>;
    }
    const value = role[column.key] ?? 0;
    const className = `nd1-hiring-table__metric-btn${
      column.highlight && value > 0 ? " nd1-hiring-table__metric-btn--new" : ""
    }`;
    return (
      <button
        type="button"
        className={className}
        onClick={() =>
          persistDashboardAndGoToRoleWorkspace(role.id, {
            selectedApplicantIndex: column.index,
          })
        }
        aria-label={`${column.label}: ${value} applicants for ${role.title}`}
      >
        {value}
      </button>
    );
  };

  const renderRowActions = (role) => {
    const isDraft = role.status === "draft";
    const isClosed = role.status === "closed";
    const draftPostId = String(role.jobPostId || role.id || "").trim();

    if (isDraft) {
      return (
        <div className="nd1-hiring-table__row-actions">
          <button
            type="button"
            className="nd1-hiring-btn-sm nd1-hiring-btn-sm--outline"
            onClick={() =>
              navigate(`/hiring/jobs/new?draft=${encodeURIComponent(role.jobPostId || role.id)}`)
            }
          >
            Edit Draft
          </button>
          <button
            type="button"
            className="nd1-hiring-table__delete-btn"
            aria-label={`Delete draft for ${role.title}`}
            title="Delete draft"
            disabled={deletingDraftId === draftPostId}
            onClick={(event) => openDraftDeleteModal(event, role)}
          >
            <Trash2 size={18} aria-hidden="true" />
          </button>
        </div>
      );
    }

    if (isClosed) {
      return (
        <div className="nd1-hiring-table__row-actions">
          <button
            type="button"
            className="nd1-hiring-btn-sm nd1-hiring-btn-sm--outline"
            onClick={() => persistDashboardAndGoToRoleWorkspace(role.id)}
          >
            View
          </button>
        </div>
      );
    }

    return (
      <div className="nd1-hiring-table__row-actions">
        <div className="nd1-hiring-table__actions-inner">
          <button
            type="button"
            className="nd1-hiring-btn-sm nd1-hiring-btn-sm--outline"
            onClick={(event) => handleViewJobPost(event, role)}
          >
            View
          </button>
          <button
            type="button"
            className="nd1-hiring-btn-sm nd1-hiring-btn-sm--accent"
            onClick={() => persistDashboardAndGoToRoleWorkspace(role.id)}
          >
            Manage
          </button>
        </div>
      </div>
    );
  };

  return (
    <RecruitmentShell currentUser={currentUser} setCurrentUser={setCurrentUser} authToken={authToken}>
      <main className="nd1-shell nd1-hiring-main">
        <section className="nd1-hiring-welcome" aria-labelledby="hiring-welcome-title">
          <div className="nd1-hiring-welcome__copy">
            <h1 id="hiring-welcome-title" className="nd1-hiring-welcome__title">
              Welcome back, {displayName}
            </h1>
            <p className="nd1-hiring-welcome__subtitle">
              Here&apos;s what&apos;s happening with your recruitment pipeline.
            </p>
          </div>
          <div className="nd1-hiring-stats" aria-label="Recruitment summary">
            <article className="nd1-hiring-stat-card">
              <div className="nd1-hiring-stat-card__head">
                <span className="nd1-hiring-stat-card__icon-wrap nd1-hiring-stat-card__icon-wrap--green">
                  <Briefcase className="nd1-hiring-stat-card__icon" aria-hidden="true" />
                </span>
              </div>
              <p className="nd1-hiring-stat-card__value nd1-hiring-stat-card__value--green">
                {jobPostStatusTotals.active}
              </p>
              <p className="nd1-hiring-stat-card__label">Active Roles</p>
              <p className="nd1-hiring-stat-card__meta">{draftSubtext}</p>
            </article>
            <article className="nd1-hiring-stat-card">
              <div className="nd1-hiring-stat-card__head">
                <span className="nd1-hiring-stat-card__icon-wrap nd1-hiring-stat-card__icon-wrap--orange">
                  <Users className="nd1-hiring-stat-card__icon" aria-hidden="true" />
                </span>
              </div>
              <p className="nd1-hiring-stat-card__value nd1-hiring-stat-card__value--orange">
                {applicantStatsLoading ? "…" : applicantStats.totalApplicants}
              </p>
              <p className="nd1-hiring-stat-card__label">Total Applicants</p>
              <p className="nd1-hiring-stat-card__meta">
                {applicantStatsLoading ? "Loading…" : `${applicantStats.newThisWeek} new this week`}
              </p>
            </article>
            <article className="nd1-hiring-stat-card">
              <div className="nd1-hiring-stat-card__head">
                <span className="nd1-hiring-stat-card__icon-wrap nd1-hiring-stat-card__icon-wrap--orange">
                  <TrendingUp className="nd1-hiring-stat-card__icon" aria-hidden="true" />
                </span>
              </div>
              <p className="nd1-hiring-stat-card__value nd1-hiring-stat-card__value--orange">
                {applicantStatsLoading ? "…" : applicantStats.totalNewApplicants}
              </p>
              <p className="nd1-hiring-stat-card__label">New Applicants</p>
            </article>
            <article className="nd1-hiring-stat-card nd1-hiring-stat-card--billing">
              <div className="nd1-hiring-stat-card__head">
                <span className="nd1-hiring-stat-card__icon-wrap nd1-hiring-stat-card__icon-wrap--blue">
                  <CreditCard className="nd1-hiring-stat-card__icon" aria-hidden="true" />
                </span>
              </div>
              <p className="nd1-hiring-stat-card__value nd1-hiring-stat-card__value--blue">
                {billingLoading && !billingSummary ? "…" : billingStat.value}
              </p>
              <p className="nd1-hiring-stat-card__label">{billingStat.label}</p>
              <p className="nd1-hiring-stat-card__meta">{billingStat.meta}</p>
              <button
                type="button"
                className="nd1-hiring-stat-card__cta"
                onClick={() => openBillingCta(billingStat.ctaMode)}
                disabled={billingLoading && !billingSummary}
              >
                {billingStat.cta}
              </button>
            </article>
          </div>
        </section>

        <section className="nd1-hiring-roles" aria-labelledby="hiring-roles-title">
          <div className="nd1-hiring-roles-card">
          <div className="nd1-hiring-section-head">
            <h3 id="hiring-roles-title" className="nd1-hiring-section-head__title">
              Advertised Roles ({totalAdvertisedRolesCount})
            </h3>
            <div className="nd1-hiring-section-head__actions">
              <button
                type="button"
                className="nd1-btn nd1-btn--accent"
                onClick={() => navigate("/hiring/jobs/new")}
              >
                + Create New Job
              </button>
            </div>
          </div>

          <div className="nd1-hiring-toolbar">
            <label className="nd1-hiring-search">
              <Search className="nd1-hiring-search__icon" aria-hidden="true" />
              <input
                type="search"
                className="nd1-hiring-search__input"
                placeholder="Search by job title, keyword or location"
                value={roleSearchInput}
                onChange={handleSearchChange}
                aria-label="Search roles"
              />
            </label>
            <button
              type="button"
              className={`nd1-hiring-toolbar__btn nd1-hiring-toolbar__filters-btn${
                filtersOpen ? " nd1-hiring-toolbar__btn--open" : ""
              }`}
              aria-expanded={filtersOpen}
              aria-controls="hiring-dashboard-filters-panel"
              onClick={toggleFiltersPanel}
            >
              <Filter size={17} aria-hidden="true" />
              Filters
              {activeStatusFilterCount > 0 ? (
                <span className="nd1-hiring-toolbar__filters-badge">{activeStatusFilterCount}</span>
              ) : null}
              <ChevronDown
                size={16}
                aria-hidden="true"
                className={`nd1-hiring-toolbar__filters-chevron${
                  filtersOpen ? " nd1-hiring-toolbar__filters-chevron--open" : ""
                }`}
              />
            </button>
          </div>

          {filtersOpen ? (
            <HiringDashboardFiltersPanel
              draftStatusFilter={draftStatusFilter}
              setDraftStatusFilter={setDraftStatusFilter}
              draftSortBy={draftSortBy}
              setDraftSortBy={setDraftSortBy}
              jobPostStatusTotals={jobPostStatusTotals}
              onApply={applyFilters}
              onClear={clearFilters}
              onClose={() => setFiltersOpen(false)}
            />
          ) : null}

          {jobsError ? <p className="nd1-hiring-empty">{jobsError}</p> : null}

          <div className="nd1-hiring-table-wrap">
            <div className="nd1-hiring-table-scroll">
            <table className="nd1-hiring-table">
              <thead>
                <tr>
                  <th scope="col">Job Title</th>
                  {METRIC_COLUMNS.map((column) => (
                    <th key={column.key} scope="col" className="nd1-hiring-table__metric-head">
                      <span className="nd1-hiring-table__metric-head-inner">
                        <RecruiterApplicantIcon
                          name={column.icon}
                          className="nd1-hiring-table__metric-icon"
                        />
                        {column.label}
                      </span>
                    </th>
                  ))}
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRoles.length === 0 ? (
                  <tr>
                    <td colSpan={METRIC_COLUMNS.length + 2}>
                      <div className="nd1-hiring-empty">
                        {loadingJobs ? (
                          <JobuwinPageLoading
                            compact
                            title="Loading roles…"
                            subtitle="Fetching your advertised roles."
                          />
                        ) : (
                          <p>No roles match your filters right now.</p>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  visibleRoles.map((role) => (
                    <tr
                      key={role.id}
                      className="nd1-hiring-table__row nd1-hiring-table__row--clickable"
                      tabIndex={0}
                      role="link"
                      aria-label={getRoleRowAriaLabel(role)}
                      onClick={(event) => handleRoleRowClick(event, role)}
                      onKeyDown={(event) => handleRoleRowKeyDown(event, role)}
                    >
                      <td className="nd1-hiring-table__job">
                        <div className="nd1-hiring-table__job-inner">
                          <span
                            className={`nd1-hiring-table__status nd1-hiring-table__status--${role.status}`}
                          >
                            <HiringRoleStatusGlyph variant={role.status} size={12} />
                            {hiringRoleStatusLabel(role.status)}
                          </span>
                          <div className="nd1-hiring-table__job-copy">
                            <span className="nd1-hiring-table__title">{role.title}</span>
                            {role.location || role.updatedAt ? (
                              <p className="nd1-hiring-table__meta nd1-hiring-table__meta-line">
                                {role.location ? (
                                  <>
                                    <MapPin className="nd1-hiring-table__pin" aria-hidden="true" />
                                    {role.location}
                                  </>
                                ) : null}
                                {role.location && role.updatedAt ? (
                                  <span className="nd1-hiring-table__meta-sep" aria-hidden="true">
                                    {" "}
                                    •{" "}
                                  </span>
                                ) : null}
                                {role.updatedAt ? formatRelativeUpdated(role.updatedAt) : null}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      {METRIC_COLUMNS.map((column) => (
                        <td key={column.key} className="nd1-hiring-table__metric">
                          {renderMetricCell(role, column)}
                        </td>
                      ))}
                      <td className="nd1-hiring-table__actions">{renderRowActions(role)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>

          {sortedRoles.length > 0 ? (
            <div className="nd1-hiring-list-footer">
              <p className="nd1-hiring-list-footer__meta">
                Showing 1 to {visibleRoles.length} of {sortedRoles.length} roles
              </p>
            </div>
          ) : null}

          {loadingJobs && jobPosts.length > 0 ? (
            <JobuwinPageLoading
              compact
              title="Loading more roles…"
              subtitle="Fetching the next batch from your account."
            />
          ) : null}

          {showLoadMoreButton ? (
            <div className="nd1-hiring-load-more-wrap">
              <button type="button" className="nd1-btn nd1-btn--outline" onClick={() => runLoadMore()}>
                Load more
              </button>
            </div>
          ) : null}

          <div ref={loadMoreSentinelRef} className="hiring-dashboard-scroll-sentinel" aria-hidden="true" />

          {!loadingJobs && !jobsError && sortedRoles.length > 0 && isFullyLoadedEnd ? (
            <p className="nd1-hiring-end-of-list" role="status">
              You&apos;re at the end
            </p>
          ) : null}
          </div>
        </section>
      </main>

      {draftDeleteModal ? (
        <div className="apply-help-overlay" role="presentation" onClick={closeDraftDeleteModal}>
          <div
            className="apply-help-modal hiring-draft-delete-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hiring-draft-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="apply-help-close"
              onClick={closeDraftDeleteModal}
              aria-label="Close"
              disabled={Boolean(deletingDraftId)}
            >
              ×
            </button>
            <h2 id="hiring-draft-delete-title" className="apply-help-title">
              Delete this draft?
            </h2>
            <div className="apply-help-body hiring-draft-delete-modal-body">
              <p>
                <strong>{draftDeleteModal.title}</strong> will be removed permanently. You can&apos;t undo this.
              </p>
            </div>
            <div className="hiring-draft-delete-modal-actions">
              <button
                type="button"
                className="hiring-draft-delete-cancel-button"
                onClick={closeDraftDeleteModal}
                disabled={Boolean(deletingDraftId)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="nd1-btn nd1-btn--accent hiring-draft-delete-confirm-button"
                onClick={() => void confirmDraftDelete()}
                disabled={Boolean(deletingDraftId)}
              >
                {deletingDraftId === draftDeleteModal.postId ? "Deleting…" : "Delete draft"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <HiringBuyJobsModal
        open={buyJobsModalOpen}
        billingSummary={billingSummary}
        onClose={() => setBuyJobsModalOpen(false)}
      />
      <HiringManagePlanModal open={managePlanModalOpen} onClose={() => setManagePlanModalOpen(false)} />
    </RecruitmentShell>
  );
}

export default HiringDashboardPage;
